import type { MapBounds } from '@map-ui';
import type L from 'leaflet';
import {
  ProgrammaticMoveContext,
  sanitizeMapBounds,
  useProgrammaticMove,
} from '@map-ui';
import { joinCss } from '@/shared/lib/css-utils';
import 'leaflet/dist/leaflet.css';
import type { ReactNode } from 'react';
import { useRef } from 'react';
import { MapContainer, useMapEvents } from 'react-leaflet';
import { BaseTileLayer } from './base-tile-layer';
import css from './leaflet-map.module.scss';

const FALLBACK_CENTER: [number, number] = [20, 0]; // world view
const FALLBACK_ZOOM = 2;

const toBounds = (map: L.Map): MapBounds => {
  const b = map.getBounds();
  const ne = b.getNorthEast();
  const sw = b.getSouthWest();
  // Leaflet can return unwrapped/out-of-range coords when zoomed out;
  // sanitise so the emitted bbox is always a valid lat/lng box.
  return sanitizeMapBounds({
    northEastLat: ne.lat,
    northEastLng: ne.lng,
    southWestLat: sw.lat,
    southWestLng: sw.lng,
  });
};

const MoveEndListener = ({
  onMoveEnd,
}: {
  onMoveEnd: (bounds: MapBounds) => void;
}) => {
  const programmaticRef = useProgrammaticMove();
  useMapEvents({
    moveend: (event) => {
      // Skip fits/region recentres (flagged programmatic) — only user
      // pans/zooms and place-search should drive a bounds refetch.
      if (programmaticRef?.current) {
        programmaticRef.current = false;
        return;
      }
      onMoveEnd(toBounds(event.target));
    },
  });
  return null;
};

type Props = {
  center?: [number, number];
  zoom?: number;
  className?: string;
  scrollWheelZoom?: boolean;
  // Fires on every pan/zoom settle with the new viewport bounds (bbox
  // shape). Hosts wire this into their filter state.
  onMoveEnd?: (bounds: MapBounds) => void;
  // Leaflet-aware children: markers, <FitBounds>, <FlyTo>, overlays.
  children?: ReactNode;
};

// Generic Leaflet canvas wrapper: branded Mapbox tiles + the default
// (top-left) Leaflet zoom control + an optional moveend bounds callback.
// Domain-agnostic — knows nothing about products or itineraries. Recentre/
// fit is driven by child helpers (<FitBounds>, <FlyTo>) so this stays
// uncontrolled after first paint.
//
// Legacy (V2) parity: `attributionControl={false}` hides the bottom-right
// "Leaflet | © Mapbox" text; `preferCanvas` + the `whenReady` tuning
// (`updateWhenZooming`/`updateWhenIdle`/`zoomAnimationThreshold`) mirror the
// legacy `components/map/map.tsx` for smooth pans/zooms with many markers.
export const LeafletMap = ({
  center,
  zoom,
  className,
  scrollWheelZoom = true,
  onMoveEnd,
  children,
}: Props) => {
  // Shared flag so child <FitBounds> can mark its recentres programmatic;
  // MoveEndListener reads it to suppress the bounds callback for those.
  const programmaticRef = useRef(false);
  const mapRef = useRef<L.Map | null>(null);
  return (
    <MapContainer
      attributionControl={false}
      center={center ?? FALLBACK_CENTER}
      className={joinCss(css.root, className)}
      preferCanvas
      ref={mapRef}
      scrollWheelZoom={scrollWheelZoom}
      whenReady={() => {
        const map = mapRef.current;
        if (!map) return;
        // Legacy perf tuning: lower the zoom-animation threshold for smoother
        // low-zoom transitions. The matching tile options
        // (`updateWhenZooming`/`updateWhenIdle`) live on <BaseTileLayer> —
        // that's where Leaflet actually reads them.
        map.options.zoomAnimationThreshold = 4;
      }}
      zoom={zoom ?? FALLBACK_ZOOM}
    >
      <BaseTileLayer />
      <ProgrammaticMoveContext.Provider value={programmaticRef}>
        {onMoveEnd ? <MoveEndListener onMoveEnd={onMoveEnd} /> : null}
        {children}
      </ProgrammaticMoveContext.Provider>
    </MapContainer>
  );
};
